import { Overlay, OverlayRef } from "@angular/cdk/overlay";
import { TemplatePortal } from "@angular/cdk/portal";
import {
  ChangeDetectionStrategy,
  Component,
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Optional,
  Output,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from "@angular/core";

/**
 * Menu source. Holds a TemplateRef rendered into an overlay when
 * a [bvMenuTriggerFor] is activated.
 *
 *   <bv-menu #actions>
 *     <button bv-menu-item (click)="rename()">Rename</button>
 *     <button bv-menu-item (click)="remove()">Delete</button>
 *   </bv-menu>
 *   <button bv-button [bvMenuTriggerFor]="actions">Actions</button>
 */
@Component({
    selector: "bv-menu",
    template: `
    <ng-template>
      <div class="bv-menu" role="menu">
        <ng-content></ng-content>
      </div>
    </ng-template>
  `,
    styles: [
        `
      :host {
        display: none;
      }

      .bv-menu {
        display: flex;
        flex-direction: column;
        background: var(--bg-surface-alt);
        border: 1px solid var(--bg-separator);
        border-radius: var(--radius-md);
        box-shadow: var(--shadow-md);
        font-family: var(--font-sans);
        padding: var(--space-1);
        min-width: 180px;
      }
    `,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class BvMenuComponent {
  @ViewChild(TemplateRef, { static: true }) templateRef!: TemplateRef<unknown>;
  @Output() closed = new EventEmitter<void>();

  /** Items call this on click; the trigger directive subscribes. */
  requestClose(): void {
    this.closed.emit();
  }
}

/**
 * Menu row. Apply to a `<button>` inside a `<bv-menu>`. Clicks fire
 * naturally and also close the parent menu.
 *
 *   <button bv-menu-item (click)="action()">Label</button>
 */
@Component({
    selector: "button[bv-menu-item], a[bv-menu-item]",
    template: "<ng-content></ng-content>",
    styles: [
        `
      :host {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        box-sizing: border-box;
        width: 100%;
        padding: var(--space-2) var(--space-3);
        background: transparent;
        color: var(--text-secondary);
        font-family: var(--font-sans);
        font-size: var(--text-sm);
        line-height: var(--leading-snug);
        text-align: left;
        text-decoration: none;
        border: none;
        border-radius: var(--radius-sm);
        cursor: pointer;
        transition:
          background var(--duration-fast) var(--ease-default),
          color var(--duration-fast) var(--ease-default);
      }

      :host:hover,
      :host:focus {
        background: var(--bg-hover);
        color: var(--text-primary);
        outline: none;
      }

      :host:disabled,
      :host[disabled] {
        opacity: 0.4;
        cursor: not-allowed;
        pointer-events: none;
      }
    `,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class BvMenuItemComponent {
  constructor(@Optional() private menu: BvMenuComponent | null) {}

  @HostListener("click")
  onClick(): void {
    this.menu?.requestClose();
  }
}

/**
 * Trigger that opens a `<bv-menu>` in a CDK overlay anchored below
 * the host element.
 */
@Directive({ selector: "[bvMenuTriggerFor]" })
export class BvMenuTriggerForDirective implements OnDestroy {
  @Input("bvMenuTriggerFor") menu!: BvMenuComponent;

  private overlayRef: OverlayRef | null = null;
  private closeSub?: { unsubscribe: () => void };

  constructor(
    private overlay: Overlay,
    private elementRef: ElementRef<HTMLElement>,
    private vcr: ViewContainerRef
  ) {}

  @HostListener("click", ["$event"])
  onClick(event: Event): void {
    event.stopPropagation();
    if (this.overlayRef) {
      this.close();
    } else {
      this.open();
    }
  }

  private open(): void {
    if (!this.menu) return;

    this.overlayRef = this.overlay.create({
      positionStrategy: this.overlay
        .position()
        .flexibleConnectedTo(this.elementRef)
        .withPositions([
          {
            originX: "start",
            originY: "bottom",
            overlayX: "start",
            overlayY: "top",
            offsetY: 4,
          },
          {
            originX: "start",
            originY: "top",
            overlayX: "start",
            overlayY: "bottom",
            offsetY: -4,
          },
        ]),
      panelClass: "bv-menu-panel",
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: true,
      backdropClass: "cdk-overlay-transparent-backdrop",
    });

    this.overlayRef.attach(
      new TemplatePortal(this.menu.templateRef, this.vcr)
    );
    this.overlayRef.backdropClick().subscribe(() => this.close());
    this.overlayRef.keydownEvents().subscribe((e: KeyboardEvent) => {
      if (e.key === "Escape") this.close();
    });
    this.closeSub = this.menu.closed.subscribe(() => this.close());
  }

  private close(): void {
    this.closeSub?.unsubscribe();
    this.closeSub = undefined;
    this.overlayRef?.dispose();
    this.overlayRef = null;
  }

  ngOnDestroy(): void {
    this.close();
  }
}
